import { george, i18n, pr, theme } from "aaa";
import { GdsFakeActionButton } from "components/common/GdsFakeActionButton";
import { IconTradingPreferences } from "components/util/primitives";
import { ui } from "components/util/uiHelpers";
import { observer } from "mobx-react";
import * as React from "react";
import { RouteComponentProps } from "react-router";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import { pushRoute } from "stores/RouterFunctions";
import { getDefaultKeyPressHandler } from "util/helpers";
import { TradingPreferencesCard } from "./components/TradingPreferencesCard";
import { TradingPreferencesRoutes } from "./TradingPreferencesRoutes";
import { TradingPreferencesStore } from "./TradingPreferencesStore";
import { IPreference } from "types/tradingPreferences";
import { TradingPreferencesMarketingApproval } from "./components/TradingPreferencesMarketingApproval";
import "./CustomButton.css";

const renderTradingPreferencesCardIcon = (item: IPreference) => {
    const style = {
        color: item.enabled ? theme.customColors.georgeLightBlue : theme.customColors.georgeLightGrey,
        height: 44,
        width: 44,
    };

    if (!item || !item.itemId) {
        return <img src={require("../../assets/tradingPreferences/stocks.svg")} {...style} />;
    }

    switch (item.itemId) {
        case "faf814c5-157a-4a15-881f-c7501bce8099":
            return <img src={require("../../assets/tradingPreferences/stocks.svg")} {...style} />;
        case "b2ec20c9-a439-44f3-aa9f-45e2283d6cdb":
            return <img src={require("../../assets/tradingPreferences/bonds.svg")} {...style} />;
        case "0a8549e3-c6f5-476d-a670-b835b5bf42be":
            return <img src={require("../../assets/tradingPreferences/funds.svg")} {...style} />;
        case "a5d596bf-5065-4253-b38e-09435f8ce9b8":
            return <img src={require("../../assets/tradingPreferences/alternativeInvestments.svg")} {...style} />;
        case "6ad49842-dfb7-429d-b990-739f05829d4f":
            return <img src={require("../../assets/tradingPreferences/sustainableProducts.svg")} {...style} />;
        case "1aa25a27-ffd5-4f28-ba36-cb203320b106":
            return <img src={require("../../assets/tradingPreferences/gold.svg")} {...style} />;
        default:
            return <img src={require("../../assets/tradingPreferences/stocks.svg")} {...style} />;
    }
}

const getTradingPreferencesDescription = (id?: string) => {
    if (!id) {
        return "";
    }

    switch (id) {
        case "faf814c5-157a-4a15-881f-c7501bce8099":
            return ui.__("tradingPreferences.overview.stocks.description");
        case "b2ec20c9-a439-44f3-aa9f-45e2283d6cdb":
            return ui.__("tradingPreferences.overview.bonds.description");
        case "0a8549e3-c6f5-476d-a670-b835b5bf42be":
            return ui.__("tradingPreferences.overview.funds.description");
        case "a5d596bf-5065-4253-b38e-09435f8ce9b8":
            return ui.__("tradingPreferences.overview.alternativeInvestments.description");
        case "6ad49842-dfb7-429d-b990-739f05829d4f":
            return ui.__("tradingPreferences.overview.sustainableProducts.description");
        case "1aa25a27-ffd5-4f28-ba36-cb203320b106":
            return ui.__("tradingPreferences.overview.gold.description");
        default:
            return "";
    }
}

type IProps = RouteComponentProps<{ id: string }> & i18n.InjectedIntlProps & {
    store: TradingPreferencesStore;
};

export const TradingPreferencesOverview = observer(({ store }: IProps) => {
    const [didAutofocus, setDidAutofocus] = React.useState(false);
    const ref = React.useRef<HTMLAnchorElement | null>(null);

    const isMobile = externalInterfaceStore.isGeorgeApp || ui.windowWidth < 600;

    const BackgroundComponent = externalInterfaceStore.isGeorgeApp ? george.Background : george.BackgroundWithNavBar;
    const buttonDisabled = !store.marketingInfoApproval || store.contactsStore.selectedEmailAddressId === undefined;

    const handleBack = async () => {
        window.history.back();
    };

    const sign = async () => {
        try {
            await store.postTradingPreferences();
            pushRoute(TradingPreferencesRoutes.SIGNING);
        } catch (error) {
            pushRoute("/error");
        }
    };

    React.useEffect(() => {
        if (ref.current && !didAutofocus) {
            ref.current.focus();
            setDidAutofocus(true);
        }
    }, []);

    if (store.isLoading) {
        return (
            <BackgroundComponent>
                <george.LoadingIndicator isLoading={store.isLoading} height={ui.windowHeight} />
            </BackgroundComponent>
        );
    }

    return (
        <BackgroundComponent style={{ paddingTop: 24, paddingBottom: theme.MARGIN }}>
            <george.Card
                disableTopSpacing
                style={{
                    marginTop: 16,
                    borderRadius: 8,
                    boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.05)"
                }}
            >
                <pr.A11yPopupHider>
                    <div style={{ textAlign: "center", padding: 32 }}>
                        <IconTradingPreferences style={{ width: 32, height: 32, padding: 16, }} />
                        <pr.P_22_TITLE style={{ fontWeight: 700, lineHeight: "28px", color: ui.isDarkMode ? "white" : "#0A285C", marginTop: 8 }}>
                            {ui.__("tradingPreferences.headline")}
                        </pr.P_22_TITLE>
                    </div>
                    <pr.LightSeparatorLine />
                </pr.A11yPopupHider>

                <pr.A11yPopupHider>
                    <div style={{ padding: 32 }}>
                        <pr.P_16_TITLE
                            style={{
                                color: ui.isDarkMode ? "white" : "#0A285C",
                                fontWeight: 600,
                                paddingBottom: 8,
                            }}
                        >
                            {ui.__("tradingPreferences.overview.introduction.title")}
                        </pr.P_16_TITLE>
                        <pr.P_16_CONTENT style={{ lineHeight: "24px", color: ui.isDarkMode ? "white" : "#0A285C" }}>
                            {ui.__("tradingPreferences.overview.introduction.description")}
                        </pr.P_16_CONTENT>
                    </div>
                    <pr.LightSeparatorLine />
                </pr.A11yPopupHider>

                <div style={{ padding: isMobile ? "32px 8px" : 32 }}>
                    <pr.A11yPopupHider>
                        <pr.P_16_TITLE
                            style={{
                                color: ui.isDarkMode ? "white" : "#0A285C",
                                fontWeight: 600,
                                paddingBottom: 8,
                            }}
                        >
                            {ui.__("tradingPreferences.overview.selection.title")}
                        </pr.P_16_TITLE>
                    </pr.A11yPopupHider>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "wrap",
                            gap: isMobile ? 12 : 16,
                            justifyContent: "center",
                            marginTop: 16
                        }}
                    >
                        {store.preferences.map((item) => (
                            <TradingPreferencesCard
                                key={item.itemId}
                                hoverText={getTradingPreferencesDescription(item.itemId)}
                                icon={renderTradingPreferencesCardIcon(item)}
                                id={item.itemId}
                                isMobile={isMobile}
                                isSelected={item.enabled}
                                name={item.itemName}
                                onClick={store.togglePreference}
                            />
                        ))}
                    </div>
                </div>

                <pr.LightSeparatorLine />
                {store.isDataConsentLoading && <george.LoadingIndicator isLoading={store.isDataConsentLoading} />}
                {!store.isDataConsentLoading && <TradingPreferencesMarketingApproval approval={store.marketingInfoApproval} consents={store.dataConsents?.consents} onSuccess={store.refetchDataConsent} />}

                <pr.A11yPopupHider>
                    <div style={{ textAlign: "center", padding: 32 }}>
                        <GdsFakeActionButton
                            className="custom-trading-preferences-gds-button"
                            color="primary"
                            disabled={buttonDisabled}
                            fullWidth={isMobile}
                            onClick={sign}
                        >
                            {ui.__("signing.button.start.signing")}
                        </GdsFakeActionButton>
                    </div>
                </pr.A11yPopupHider>
            </george.Card>
        </BackgroundComponent >
    );
});



// WEBPACK FOOTER //
// ./src/components/tradingPreferences/TradingPreferencesOverview.tsx